import React from 'react';
import { Zap, User } from 'lucide-react';
import { CloudNetworkIcon } from '../icons/themed';
import { useChatStore } from '../../stores/useChatStore.js';

interface AutonomyMode {
  mode: 'manual' | 'semi' | 'full';
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
}

const autonomyModes: AutonomyMode[] = [
  {
    mode: 'manual',
    label: 'Manual',
    description: 'All actions require explicit approval',
    icon: <User className="w-4 h-4" />,
    color: 'text-slate-400',
    bgColor: 'bg-slate-600/20',
    borderColor: 'border-slate-500/30'
  },
  {
    mode: 'semi',
    label: 'Assist',
    description: 'Safe operations auto-execute, critical actions need approval',
    icon: <CloudNetworkIcon className="w-4 h-4" />,
    color: 'text-violet-400',
    bgColor: 'bg-violet-600/20',
    borderColor: 'border-violet-500/30'
  },
  {
    mode: 'full',
    label: 'Automatic',
    description: 'Maximum AI control with override capability',
    icon: <Zap className="w-4 h-4" />,
    color: 'text-violet-400',
    bgColor: 'bg-violet-600/20',
    borderColor: 'border-violet-500/30'
  }
];

interface AutonomyToggleProps {
  className?: string;
}

const AutonomyToggle: React.FC<AutonomyToggleProps> = ({ 
  className = ''
}) => {
  const { beeControlMode, switchMode } = useChatStore();

  const currentMode = autonomyModes.find(mode => mode.mode === beeControlMode) || autonomyModes[0];

  const handleModeChange = (mode: 'manual' | 'semi' | 'full') => {
    if (mode !== beeControlMode) {
      switchMode(mode);
    }
  };

  return (
    <div className={`bg-slate-900 border border-gray-700 rounded-lg p-4 w-full ${className}`}>
      <div className="text-sm font-semibold text-gray-300 mb-2">
        <span>Bee Control Mode</span>
      </div>

      <div className="inline-flex w-full bg-gray-800 rounded-md overflow-hidden mb-3">
        {autonomyModes.map((mode) => (
          <button
            key={mode.mode}
            onClick={() => handleModeChange(mode.mode)}
            className={`
              flex-1 py-1 text-center text-sm font-medium
              ${beeControlMode === mode.mode 
                ? 'bg-gradient-to-r from-blue-600 to-teal-500 text-white'
                : 'text-gray-400 hover:bg-gray-700 hover:text-white'}
            `}
          >
            {mode.label}
          </button>
        ))}
      </div>

      <div className="text-xs text-gray-300 italic">
        {currentMode.description}
      </div>
    </div>
  );
};

export default AutonomyToggle;
